import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { supplyApi } from '../../lib/supplyApi';
import { formatDate, formatLitres } from '../../lib/formats';
import { libelle } from '../../lib/libelles';
import { PageHeader } from '../../components/ui/page-header';
import { Aide } from '../../components/ui/aide';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import {
  ListePropositionsReappro,
  ListeStationsAReapprovisionner,
  usePropositionsReappro,
  useStationsAReapprovisionner,
} from '../../components/stations/supply/StationsToResupply';

/**
 * Réapprovisionnement de mes stations (lot 032), groupe « Mes stations » du
 * menu Transport : nos stations à servir (autonomie des cuves, commandes en
 * cours) et les réceptions de camion attendues dans nos stations.
 */

const Panneau = ({ titre, aide, droite, children }) => (
  <section className="rounded-xl border border-border bg-card shadow-sm">
    <header className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2 pt-3">
      <h2 className="flex items-center gap-1 text-base font-semibold">
        {titre}
        {aide}
      </h2>
      {droite}
    </header>
    <div className="pb-2">{children}</div>
  </section>
);

const SupplyPage = () => {
  const navigate = useNavigate();
  const { stations, chargement } = useStationsAReapprovisionner();
  const { plan, chargement: chargementPlan } = usePropositionsReappro();
  const [receptions, setReceptions] = useState([]);
  const [chargementReceptions, setChargementReceptions] = useState(true);

  const chargerReceptions = useCallback(async () => {
    setChargementReceptions(true);
    try {
      setReceptions((await supplyApi.receptions()) || []);
    } catch {
      setReceptions([]);
    } finally {
      setChargementReceptions(false);
    }
  }, []);

  useEffect(() => {
    chargerReceptions();
  }, [chargerReceptions]);

  const basses = stations.filter((station) => station.low).length;
  const litresEnCommande = stations.reduce((total, station) => total + Number(station.pending_liters || 0), 0);

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Mes stations"
        title="Réapprovisionnement"
        description="Stations de la société à servir et réceptions de camion attendues."
        statsDisposition="ligne"
        stats={[
          { label: 'Stations', valeur: stations.length },
          { label: 'Litres en commande', valeur: litresEnCommande, unite: 'L' },
          { label: 'Réceptions attendues', valeur: receptions.length },
        ]}
        alertes={[
          basses > 0 && {
            niveau: 'attention',
            texte: `${basses} station${basses > 1 ? 's' : ''} à moins de 2 jours de vente ou sous le seuil`,
            court: 'Autonomie basse',
            nombre: basses,
          },
        ].filter(Boolean)}
        actions={
          <Button size="sm" onClick={() => navigate('/orders')}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Commander
          </Button>
        }
      />

      <Panneau
        titre="Stations à réapprovisionner"
        aide={
          <Aide titre="Stations à réapprovisionner">
            La plus pressée d’abord. Autonomie : jours de vente que couvre le stock de la cuve, au rythme des 7 derniers
            jours vendus (28 jours à défaut).
          </Aide>
        }
      >
        {chargement ? (
          <LoadingState shape="table" />
        ) : stations.length ? (
          <ListeStationsAReapprovisionner stations={stations} />
        ) : (
          <EmptyState variant="empty" title="Aucune station de la société" className="border-0" />
        )}
      </Panneau>

      <Panneau
        titre="À livrer en priorité"
        aide={
          <Aide titre="À livrer en priorité">
            Cuves à moins de 2 jours de vente. Les litres proposés remplissent la cuve jusqu’à 90 % de sa capacité.
            Les stations d’une même ville sont regroupées pour remplir un même camion.
          </Aide>
        }
        droite={plan.total_liters ? <Badge variant="warning">{formatLitres(plan.total_liters)}</Badge> : null}
      >
        {chargementPlan ? (
          <LoadingState shape="table" />
        ) : plan.groups.length ? (
          <ListePropositionsReappro groupes={plan.groups} />
        ) : (
          <EmptyState variant="empty" title="Aucune cuve à moins de 2 jours de vente" className="border-0" />
        )}
      </Panneau>

      <Panneau titre="Réceptions à faire">
        {chargementReceptions ? (
          <LoadingState shape="table" />
        ) : receptions.length ? (
          <ul className="divide-y divide-border/60" data-testid="receptions-a-faire">
            {receptions.map((reception) => (
              <li key={reception.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
                <span className="min-w-0">
                  <span className="font-medium text-foreground">{reception.station_name}</span>
                  <span className="text-muted-foreground">
                    {' · '}
                    {[reception.tank_name, reception.product_name, reception.truck_label, reception.delivery_note_number]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums text-muted-foreground">{formatLitres(reception.delivered_liters_15c)}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(reception.expected_at || reception.created_at)}</span>
                  <Badge variant={reception.status === 'IN_PROGRESS' ? 'info' : 'warning'}>
                    {libelle('station_reception', reception.status)}
                  </Badge>
                  {reception.trip_id ? (
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/trips/${reception.trip_id}`}>Voyage</Link>
                    </Button>
                  ) : null}
                  <Button asChild size="sm" variant="outline">
                    <Link to={`/stations/receptions/${reception.id}`}>Réceptionner</Link>
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState variant="empty" title="Aucune réception attendue" className="border-0" />
        )}
      </Panneau>
    </div>
  );
};

export default SupplyPage;
